/*
 * UserCard
 * @module components
 * @flow
 */

import React from 'react';
import styled from 'styled-components/macro';
import { palette } from 'styled-theme';
// $FlowIssue
import { FormattedMessage } from 'react-intl';

import { Block, Avatar, DataValue } from '~/components';

import messages from './messages';

export type UserCardProps = {
  scoreValue?: number,
  rewardValue?: number,
  name: string,
  src?: string,
  email?: string,
  onClick?: () => void,
};

const UserCardWrapper = styled(Block)`
  padding: 8px 16px;
  width: 325px;
  height: 76px;
  font-size: 16px;
  color: ${palette('grayscale', 0)};
`;

const UserCardInfo = styled.div`
  flex: 1;
  padding: 9px 0 0 12px;
`;

const UserCardTitle = styled.h4`
  margin-bottom: 10px;
  font-weight: 600;
`;

const UserCardRating = styled.div`
  display: inline-flex;
  align-items: center;
  width: 50%;
`;

const UserCardRatingAvatar = styled(Avatar).attrs(() => ({
  small: true,
}))`
  margin-right: 7px;
  color: #fff;
  background-color: #748ead;
`;

const UserCardRatingRewardAvatar = styled(UserCardRatingAvatar)`
  background-color: #1175bb;
`;

/**
 * UserCard React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {string} [props.scoreValue = 0] - user score value
 * @param {boolean} [props.rewardValue = 0] - user reward value
 * @param {string} [props.name] - user name
 * @param {string} [props.src] - link to the avatar image
 * @param {string} [props.email] - user email
 * @param {Function} [props.onClick] - click callback
 * @example <caption>Simple usage</caption>
 * <UserCard scoreValue={123564} rewardValue={45} name="Yann Russell" src="http://i.pravatar.cc/150?img=47" />
 * @returns {React.Node<*>} React-component
 */
export const UserCard = ({
  scoreValue,
  rewardValue,
  name,
  onClick,
  ...props
}: UserCardProps) => (
  <UserCardWrapper testid="usercard" onClick={onClick}>
    <Avatar {...props}>{name}</Avatar>
    <UserCardInfo>
      <UserCardTitle>{name}</UserCardTitle>
      <div>
        <UserCardRating>
          <UserCardRatingAvatar>
            <FormattedMessage {...messages.score} />
          </UserCardRatingAvatar>
          <DataValue value={scoreValue || 0} />
        </UserCardRating>
        <UserCardRating>
          <UserCardRatingRewardAvatar>
            <FormattedMessage {...messages.reward} />
          </UserCardRatingRewardAvatar>
          <DataValue
            // eslint-disable-next-line react/style-prop-object
            style="percent"
            value={(rewardValue || 0) / 100}
            percents
          />
        </UserCardRating>
      </div>
    </UserCardInfo>
  </UserCardWrapper>
);

UserCard.defaultProps = {
  scoreValue: 0,
  rewardValue: 0,
  onClick: () => {},
};
